import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { 
  Download, Upload, Database, RefreshCw, AlertTriangle, 
  CheckCircle, Users, FileText, FolderOpen, Clock, HardDrive,
  Cloud, Settings, Trash2, ExternalLink, Play, History
} from "lucide-react";
import { toast } from "sonner";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (qui si usava date-fns).
import { formatDate, formatDateTime } from "../lib/format";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import DataStateError from "../components/DataStateError";
import { useConferma } from "../components/ConfirmProvider";

export default function BackupPage() {
  const [stats, setStats] = useState({ clients: 0, policies: 0, documents: 0 });
  const conferma = useConferma();
  const [loading, setLoading] = useState(true);
  // Distingue "non c'e' niente" da "il caricamento e' fallito": senza questo
  // un guasto di rete sembrava una pagina vuota.
  const [loadError, setLoadError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [backingUpZipToDrive, setBackingUpZipToDrive] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreMode, setRestoreMode] = useState("merge");
  const [restoreConfirmText, setRestoreConfirmText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [driveConnected, setDriveConnected] = useState(false);
  const [backupSettings, setBackupSettings] = useState({
    enabled: false,
    frequency: "weekly",
    max_backups: 3,
    backup_app_zip: false,
    app_zip_frequency: "weekly",
    app_zip_max_backups: 3
  });
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [appVersion, setAppVersion] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [restoringSnapshot, setRestoringSnapshot] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
    fetchAppVersion();
  }, []);

  const fetchAppVersion = async () => {
    try {
      const response = await fetch("/version.json?" + Date.now());
      if (response.ok) {
        const data = await response.json();
        setAppVersion(data);
      }
    } catch (err) {
      console.error("Error fetching version:", err);
    }
  };

  const fetchData = async () => {
    setLoadError(false);
    setLoading(true);
    try {
      const [statsRes, driveRes, settingsRes, snapRes] = await Promise.all([
        axios.get("/api/backup/stats"),
        axios.get("/api/backup/mega/status"),
        axios.get("/api/backup/settings"),
        axios.get("/api/backup/snapshots")
      ]);
      
      setStats(statsRes.data.stats);
      setDriveConnected(driveRes.data.connected);
      setBackupSettings(settingsRes.data);
      setSnapshots(snapRes.data.snapshots || []);
    } catch (error) {
      setLoadError(true);
      toast.error("Errore nel caricamento dei dati backup");
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreSnapshot = async (snapshotId) => {
    if (!(await conferma({
      titolo: "Ripristinare questo punto di sicurezza?",
      descrizione:
        "Clienti e polizze torneranno allo stato dello snapshot. Prima di procedere verrà creato automaticamente " +
      "un nuovo punto di sicurezza con la situazione attuale, quindi l'operazione resta annullabile.",
    }))) return;
    setRestoringSnapshot(snapshotId);
    try {
      const res = await axios.post(`/api/backup/snapshots/${snapshotId}/restore`);
      toast.success(
        `Ripristinato: ${res.data.restored.clients} clienti, ${res.data.restored.policies} polizze`
      );
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel ripristino dello snapshot");
    } finally {
      setRestoringSnapshot(null);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await axios.get("/api/backup/data", {
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, "");
      link.setAttribute("download", `insureassist_backup_${timestamp}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      await axios.post("/api/backup/log");
      toast.success("Backup locale completato!");
      fetchData();
    } catch (error) {
      toast.error("Errore durante l'export dei dati");
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith(".json")) {
        toast.error("Seleziona un file JSON di backup valido");
        return;
      }
      setSelectedFile(file);
      setRestoreConfirmText("");
      setRestoreDialogOpen(true);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) return;
    
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      
      const response = await axios.post(
        `/api/backup/restore?mode=${restoreMode}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      
      const result = response.data;
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-semibold">✅ Ripristino completato!</span>
          <span className="text-sm">Clienti: {result.restored.clients}</span>
          <span className="text-sm">Polizze: {result.restored.policies}</span>
          {result.restored.skipped > 0 && (
            <span className="text-xs text-muted-foreground">
              ({result.restored.skipped} record saltati)
            </span>
          )}
          {result.snapshot?.snapshot_id && (
            <span className="text-xs text-muted-foreground">
              Punto di sicurezza creato: {result.snapshot.snapshot_id}
            </span>
          )}
        </div>,
        { duration: 5000 }
      );
      
      setRestoreDialogOpen(false);
      setSelectedFile(null);
      setRestoreConfirmText("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il ripristino");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.post("/api/backup/settings", backupSettings);
      toast.success("Impostazioni backup salvate!");
      setSettingsDialogOpen(false);
    } catch (error) {
      toast.error("Errore salvataggio impostazioni");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFrequencyLabel = (freq) => {
    switch (freq) {
      case "daily": return "Giornaliero";
      case "weekly": return "Settimanale";
      case "monthly": return "Mensile";
      default: return freq;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="backup-page-loading">
        <div className="flex justify-between items-start">
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-72" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-12" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Main Content Skeleton */}
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32 mb-2" />
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-40 mb-2" />
              <Skeleton className="h-4 w-56" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-6" data-testid="pagina-errore-caricamento">
        <DataStateError
          titolo="Non riesco a caricare i backup"
          messaggio="I dati non sono andati persi: e' il collegamento che non ha risposto."
          onRetry={fetchData}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="backup-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground tracking-tight">
            Backup & Ripristino
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Backup automatico su MEGA con rotazione
          </p>
        </div>
        <Button variant="outline" onClick={() => setSettingsDialogOpen(true)}>
          <Settings className="mr-2 h-4 w-4" />
          Impostazioni
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Clienti</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.clients}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Polizze</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.policies}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Documenti</CardTitle>
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.documents}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">MEGA</CardTitle>
            <Cloud className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Badge variant={driveConnected ? "default" : "secondary"}>
              {driveConnected ? "Connesso" : "Non connesso"}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Punti di sicurezza (snapshot pre-ripristino) */}
      {snapshots.length > 0 && (
        <Card data-testid="snapshots-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Punti di sicurezza
            </CardTitle>
            <CardDescription>
              Creati automaticamente prima di ogni ripristino "Sostituisci": servono ad annullarlo se qualcosa va storto.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[180px] pr-3">
              <div className="space-y-2">
                {snapshots.map((s) => (
                  <div key={s.snapshot_id} className="flex items-center justify-between gap-3 p-2 rounded-lg border">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{s.snapshot_id}</p>
                      <p className="text-xs text-muted-foreground">
                        {s.created_at ? formatDateTime(s.created_at) : ""} ·{" "}
                        {s.counts?.clients ?? 0} clienti · {s.counts?.policies ?? 0} polizze
                        {s.restored_at ? " · già ripristinato" : ""}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{s.reason}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={restoringSnapshot === s.snapshot_id}
                      onClick={() => handleRestoreSnapshot(s.snapshot_id)}
                      data-testid={`restore-snapshot-${s.snapshot_id}`}
                    >
                      {restoringSnapshot === s.snapshot_id ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <History className="h-4 w-4 mr-2" />
                      )}
                      Ripristina
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Auto Backup Status */}
      {backupSettings.enabled && (
        <Card className="border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950">
          <CardContent className="flex items-center gap-3 pt-6">
            <RefreshCw className="h-5 w-5 text-blue-600" />
            <div>
              <p className="font-medium text-blue-800 dark:text-blue-200">
                Backup automatico attivo: {getFrequencyLabel(backupSettings.frequency)}
              </p>
              <p className="text-sm text-blue-600 dark:text-blue-400">
                Mantiene gli ultimi {backupSettings.max_backups} backup su MEGA
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Actions */}
      <div className="grid gap-6 md:grid-cols-2">
                {/* Local Backup */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-primary" />
              Backup Locale
            </CardTitle>
            <CardDescription>
              Scarica i dati sul tuo dispositivo come file JSON
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              onClick={handleExportData} 
              disabled={exporting}
              variant="outline"
              className="w-full"
              data-testid="export-data-btn"
            >
              {exporting ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Esportazione...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Scarica Backup Locale
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Restore from File */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-primary" />
            Ripristina da File
          </CardTitle>
          <CardDescription>
            Importa dati da un file di backup JSON locale
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button 
            onClick={() => fileInputRef.current?.click()}
            variant="outline"
            className="w-full"
            data-testid="import-data-btn"
          >
            <Upload className="mr-2 h-4 w-4" />
            Seleziona File di Backup
          </Button>
        </CardContent>
      </Card>

      {/* Download Application */}
      <Card className="border-muted">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5" />
            Scarica Applicazione
          </CardTitle>
          <CardDescription>
            Pacchetto completo per installazione su Unraid
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {appVersion && (
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Versione</span>
                <Badge variant="secondary" className="text-sm">
                  v{appVersion.version}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Data rilascio</span>
                <span className="text-sm text-muted-foreground">
                  {formatDate(appVersion.release_date)}
                </span>
              </div>
              
              {appVersion.changelog && appVersion.changelog.length > 0 && (
                <div className="pt-2 border-t">
                  <p className="text-sm font-medium mb-2">Ultime modifiche:</p>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    {appVersion.changelog.slice(0, 5).map((item, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-green-500 mt-0.5">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          <Button 
            onClick={() => window.open("/api/settings/download-app", "_blank")}
            variant="default"
            className="w-full"
          >
            <Download className="mr-2 h-4 w-4" />
            {appVersion ? `Scarica InsureAssist v${appVersion.version}` : "Scarica InsureAssist"}
          </Button>
          
          <Button 
            onClick={async () => {
              if (!driveConnected) {
                toast.error("MEGA non connesso: verifica la sessione sul server");
                return;
              }
              setBackingUpZipToDrive(true);
              try {
                const response = await axios.post("/api/backup/mega/app-zip");
                toast.success(
                  <div className="flex flex-col gap-1">
                    <span className="font-semibold">Backup app su MEGA completato!</span>
                    <span className="text-sm">{response.data.file_name} ({response.data.size_mb} MB)</span>
                    {response.data.old_deleted > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {response.data.old_deleted} vecchie versioni eliminate
                      </span>
                    )}
                  </div>,
                  { duration: 5000 }
                );
              } catch (error) {
                toast.error(error.response?.data?.detail || "Errore backup app su MEGA");
              } finally {
                setBackingUpZipToDrive(false);
              }
            }}
            variant="outline"
            className="w-full"
            disabled={backingUpZipToDrive || !driveConnected}
            data-testid="backup-zip-drive-btn"
          >
            {backingUpZipToDrive ? (
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Cloud className="mr-2 h-4 w-4" />
            )}
            {backingUpZipToDrive ? "Backup in corso..." : "Salva backup app su MEGA"}
          </Button>
          
          <p className="text-xs text-muted-foreground text-center">
            Il download genera sempre la versione aggiornata. Il backup app su MEGA tiene le ultime versioni.
          </p>
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Impostazioni Backup Automatico</DialogTitle>
            <DialogDescription>
              Configura il backup automatico su MEGA
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Backup automatico</Label>
                <p className="text-sm text-muted-foreground">
                  Esegue backup periodici su MEGA
                </p>
              </div>
              <Switch
                checked={backupSettings.enabled}
                onCheckedChange={(checked) => 
                  setBackupSettings(prev => ({ ...prev, enabled: checked }))
                }
                disabled={!driveConnected}
              />
            </div>
            
            {!driveConnected && (
              <p className="text-sm text-yellow-600 bg-yellow-50 p-2 rounded">
                ⚠️ Collega MEGA per attivare il backup automatico
              </p>
            )}
            
            <div className="space-y-2">
              <Label>Frequenza backup</Label>
              <Select 
                value={backupSettings.frequency} 
                onValueChange={(value) => 
                  setBackupSettings(prev => ({ ...prev, frequency: value }))
                }
                disabled={!backupSettings.enabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Giornaliero (ogni giorno alle 3:00)</SelectItem>
                  <SelectItem value="weekly">Settimanale (ogni 7 giorni)</SelectItem>
                  <SelectItem value="monthly">Mensile (ogni 30 giorni)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Backup da mantenere</Label>
              <Select 
                value={backupSettings.max_backups?.toString()} 
                onValueChange={(value) => 
                  setBackupSettings(prev => ({ ...prev, max_backups: parseInt(value) }))
                }
                disabled={!backupSettings.enabled}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 backup</SelectItem>
                  <SelectItem value="2">2 backup</SelectItem>
                  <SelectItem value="3">3 backup (consigliato)</SelectItem>
                  <SelectItem value="5">5 backup</SelectItem>
                  <SelectItem value="10">10 backup</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                I backup pi vecchi vengono eliminati automaticamente
              </p>
            </div>

            {/* App ZIP Backup Section */}
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Label>Backup ZIP applicazione</Label>
                  <p className="text-sm text-muted-foreground">
                    Salva una copia dello ZIP dell'app su MEGA
                  </p>
                </div>
                <Switch
                  checked={backupSettings.backup_app_zip}
                  onCheckedChange={(checked) => 
                    setBackupSettings(prev => ({ ...prev, backup_app_zip: checked }))
                  }
                  disabled={!driveConnected}
                />
              </div>

              <div className="space-y-2">
                <Label>Frequenza backup ZIP</Label>
                <Select 
                  value={backupSettings.app_zip_frequency} 
                  onValueChange={(value) => 
                    setBackupSettings(prev => ({ ...prev, app_zip_frequency: value }))
                  }
                  disabled={!backupSettings.backup_app_zip}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Settimanale (ogni 7 giorni)</SelectItem>
                    <SelectItem value="monthly">Mensile (ogni 30 giorni)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2 mt-2">
                <Label>Versioni ZIP da mantenere</Label>
                <Select 
                  value={backupSettings.app_zip_max_backups?.toString()} 
                  onValueChange={(value) => 
                    setBackupSettings(prev => ({ ...prev, app_zip_max_backups: parseInt(value) }))
                  }
                  disabled={!backupSettings.backup_app_zip}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 versione</SelectItem>
                    <SelectItem value="2">2 versioni</SelectItem>
                    <SelectItem value="3">3 versioni (consigliato)</SelectItem>
                    <SelectItem value="5">5 versioni</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveSettings}>
              Salva Impostazioni
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Local Restore Dialog */}
      <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Conferma Ripristino
            </DialogTitle>
            <DialogDescription>
              File: <strong>{selectedFile?.name}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Modalità</Label>
              <Select value={restoreMode} onValueChange={setRestoreMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="merge">Unisci (mantiene dati esistenti)</SelectItem>
                  <SelectItem value="replace">Sostituisci (cancella tutto!)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {restoreMode === "replace" && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-950 space-y-2">
                <p className="text-sm text-red-700 dark:text-red-300">
                  ⚠️ Tutti i clienti e le polizze attuali verranno eliminati e sostituiti con quelli del file.
                </p>
                <p className="text-xs text-red-600 dark:text-red-400">
                  Prima di procedere viene creato automaticamente un <strong>punto di sicurezza</strong> (lo trovi nella
                  sezione "Punti di sicurezza"), quindi potrai annullare il ripristino.
                </p>
                <div className="space-y-1 pt-1">
                  <Label htmlFor="confirm-replace" className="text-xs text-red-700 dark:text-red-300">
                    Per confermare, scrivi <strong>ELIMINA</strong>
                  </Label>
                  <Input
                    id="confirm-replace"
                    value={restoreConfirmText}
                    onChange={(e) => setRestoreConfirmText(e.target.value)}
                    placeholder="ELIMINA"
                    className="h-8"
                    data-testid="restore-confirm-input"
                  />
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRestoreDialogOpen(false); setRestoreConfirmText(""); }}>
              Annulla
            </Button>
            <Button 
              onClick={handleRestore}
              disabled={importing || (restoreMode === "replace" && restoreConfirmText.trim().toUpperCase() !== "ELIMINA")}
              variant={restoreMode === "replace" ? "destructive" : "default"}
              data-testid="confirm-restore-btn"
            >
              {importing ? "Ripristino..." : "Ripristina"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drive Restore Dialog */}
    </div>
  );
}
